/** Map banking REST API rows ↔ UI field names used by banque pages. */

import { buildDefaultBankingConfig } from './saudiBankingDefaults';
import { getBrandDefaultCurrency, getBrandLocale } from '../../shared/utils/brandCurrency';

const TRANSFER_STATUS_TO_UI = { pending: 'pending', completed: 'completed', cancelled: 'rejected' };
const TRANSFER_STATUS_TO_API = { pending: 'pending', completed: 'completed', rejected: 'cancelled' };

const CHECK_STATUS_TO_UI = { issued: 'pending', cleared: 'cleared', bounced: 'bounced', cancelled: 'bounced' };
const CHECK_STATUS_TO_API = { pending: 'issued', cleared: 'cleared', bounced: 'bounced' };

function formatPeriod(dateStr, locale = getBrandLocale('ar')) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

export function mapTransferFromApi(row) {
  if (!row) return row;
  return {
    id: row.id,
    date: row.transfer_date || '',
    reference: row.reference || '',
    banqueSource: row.from_account_name || row.from_account_number || String(row.from_account_id || ''),
    banqueDestination: row.to_account_name || row.to_account_number || String(row.to_account_id || ''),
    beneficiaire: row.description || '',
    montant: Number(row.amount) || 0,
    motif: row.description || '',
    frais: Number(row.fees) || 0,
    status: TRANSFER_STATUS_TO_UI[row.status] || row.status || 'pending',
    from_account_id: row.from_account_id,
    to_account_id: row.to_account_id,
  };
}

export function mapTransferToApi(form, accounts = []) {
  const resolveAccountId = (label, explicitId) => {
    if (explicitId != null && explicitId !== '') return Number(explicitId);
    const normalized = String(label || '').trim();
    if (!normalized) return null;
    const match = accounts.find((a) => {
      const dashLabel = `${a.bank_name || ''} — ${a.account_name || a.account_number || ''}`.trim();
      const spaceLabel = `${a.bank_name || ''} ${a.account_name || ''}`.trim();
      return (
        a.account_name === normalized ||
        a.bank_name === normalized ||
        spaceLabel === normalized ||
        dashLabel === normalized ||
        String(a.id) === normalized
      );
    });
    return match ? match.id : null;
  };

  return {
    from_account_id: resolveAccountId(form.banqueSource, form.from_account_id),
    to_account_id: resolveAccountId(form.banqueDestination, form.to_account_id),
    amount: parseFloat(form.montant) || 0,
    transfer_date: form.date,
    reference: form.reference || null,
    description: form.motif || form.beneficiaire || null,
    fees: parseFloat(form.frais) || 0,
  };
}

export function mapPaymentOrderFromApi(row) {
  if (!row) return row;
  return {
    id: row.id,
    date: row.order_date || '',
    reference: row.reference || '',
    beneficiaire: row.beneficiary_name || '',
    banque: row.beneficiary_bank || row.bank_name || '',
    montant: Number(row.amount) || 0,
    motif: row.description || '',
    dateExecution: row.execution_date || '',
    status: row.status || 'pending',
    account_id: row.account_id,
    account_name: row.account_name || '',
    account_number: row.account_number || '',
    bank_name: row.bank_name || '',
    beneficiary_account: row.beneficiary_account || '',
  };
}

export function mapPaymentOrderToApi(form, accounts = []) {
  const resolveAccountId = (explicitId, label) => {
    if (explicitId != null && explicitId !== '') return Number(explicitId);
    const normalized = String(label || '').trim();
    if (!normalized) return null;
    const match = accounts.find((a) => {
      const dashLabel = `${a.bank_name || ''} — ${a.account_name || a.account_number || ''}`.trim();
      const spaceLabel = `${a.bank_name || ''} ${a.account_name || ''}`.trim();
      return (
        a.account_name === normalized ||
        a.bank_name === normalized ||
        spaceLabel === normalized ||
        dashLabel === normalized ||
        String(a.id) === normalized
      );
    });
    return match ? match.id : null;
  };

  const accountId = resolveAccountId(form.account_id, form.banque || form.account_name);
  const account = accounts.find((a) => String(a.id) === String(accountId));

  return {
    account_id: accountId != null ? accountId : undefined,
    beneficiary_name: form.beneficiaire,
    beneficiary_account: form.beneficiary_account || '00000000000',
    beneficiary_bank: form.banque || account?.bank_name || null,
    amount: parseFloat(form.montant) || 0,
    order_date: form.date,
    execution_date: form.dateExecution || null,
    reference: form.reference || null,
    description: form.motif || null,
    status: form.status || 'pending',
  };
}

export function mapCheckFromApi(row) {
  if (!row) return row;
  return {
    id: row.id,
    date: row.issue_date || '',
    numeroCheque: row.check_number || '',
    emetteur: row.payee || '',
    banque: row.bank_name || '',
    montant: Number(row.amount) || 0,
    dateEcheance: row.due_date || '',
    status: CHECK_STATUS_TO_UI[row.status] || row.status || 'pending',
    account_id: row.account_id,
    notes: row.notes || '',
  };
}

export function mapCheckToApi(form) {
  return {
    account_id: form.account_id ? Number(form.account_id) : undefined,
    check_number: form.numeroCheque,
    payee: form.emetteur,
    amount: parseFloat(form.montant) || 0,
    issue_date: form.date,
    due_date: form.dateEcheance || null,
    status: CHECK_STATUS_TO_API[form.status] || form.status || 'issued',
    notes: form.notes || null,
  };
}

export function mapStatementFromApi(row, accountsById = {}) {
  if (!row) return row;
  const account = accountsById[row.account_id] || {};
  const dateRef = row.statement_date || row.transaction_date || '';
  return {
    id: row.id,
    bankName: account.bank_name || row.bank_name || '',
    accountNumber: account.account_number || row.account_number || '',
    period: formatPeriod(dateRef),
    entryCount: 1,
    balance: Number(row.balance) || 0,
    uploadDate: row.statement_date || row.transaction_date || '',
    account_id: row.account_id,
    statement_date: row.statement_date,
    transaction_date: row.transaction_date,
    description: row.description || '',
    reference: row.reference || '',
    debit: Number(row.debit) || 0,
    credit: Number(row.credit) || 0,
    notes: row.notes || '',
  };
}

export function mapStatementToApi(form, accounts = []) {
  const account =
    accounts.find((a) => a.account_number === form.accountNumber) ||
    accounts.find((a) => a.bank_name === form.bankName);
  const date = form.uploadDate || form.transaction_date || new Date().toISOString().slice(0, 10);
  const balance = parseFloat(form.balance) || 0;
  return {
    account_id: form.account_id ? Number(form.account_id) : account?.id,
    statement_date: date,
    transaction_date: date,
    description: form.notes || form.description || form.period || 'Relevé bancaire',
    reference: form.reference || null,
    debit: parseFloat(form.debit) || 0,
    credit: parseFloat(form.credit) || balance,
    balance,
    notes: form.notes || null,
  };
}

const DEFAULT_UI_CONFIG = buildDefaultBankingConfig();

export function mapConfigFromApi(apiConfig = {}) {
  const ui = { ...(apiConfig._ui || {}) };
  return {
    ...DEFAULT_UI_CONFIG,
    ...ui,
    autoReconciliation: apiConfig.auto_reconciliation?.enabled ?? DEFAULT_UI_CONFIG.autoReconciliation,
    reconciliationTolerance: Math.round((apiConfig.auto_reconciliation?.match_tolerance ?? 0.01) * 1000) || DEFAULT_UI_CONFIG.reconciliationTolerance,
    alertOnMismatch: apiConfig.notifications?.pending_reconciliation_alert ?? DEFAULT_UI_CONFIG.alertOnMismatch,
    checkClearanceDays: apiConfig.notifications?.old_check_days ?? DEFAULT_UI_CONFIG.checkClearanceDays,
    alertExpiringChecks: apiConfig.notifications?.old_check_alert ?? DEFAULT_UI_CONFIG.alertExpiringChecks,
    expiringChecksDays: apiConfig.notifications?.old_check_days ?? DEFAULT_UI_CONFIG.expiringChecksDays,
    maxTransferAmount: apiConfig.security?.large_transfer_threshold ?? DEFAULT_UI_CONFIG.maxTransferAmount,
    requireApprovalAbove: apiConfig.security?.large_transfer_threshold ?? DEFAULT_UI_CONFIG.requireApprovalAbove,
    requireDualApproval: apiConfig.security?.require_dual_authorization ?? DEFAULT_UI_CONFIG.requireDualApproval,
    requireTwoFactorAuth: apiConfig.security?.require_dual_authorization ?? DEFAULT_UI_CONFIG.requireTwoFactorAuth,
    autoImportStatements: apiConfig.reconciliation?.auto_match_on_import ?? DEFAULT_UI_CONFIG.autoImportStatements,
    trackBankFees: true,
    feeThreshold: apiConfig.fees?.default_transfer_fee ?? DEFAULT_UI_CONFIG.feeThreshold,
    lowBalanceThreshold: apiConfig.notifications?.low_balance_threshold ?? DEFAULT_UI_CONFIG.lowBalanceThreshold,
    highTransactionThreshold: apiConfig.notifications?.high_transaction_threshold ?? DEFAULT_UI_CONFIG.highTransactionThreshold,
    unusualActivityThreshold: apiConfig.notifications?.unusual_activity_threshold ?? DEFAULT_UI_CONFIG.unusualActivityThreshold,
    defaultCurrency: apiConfig.display?.default_currency ?? DEFAULT_UI_CONFIG.defaultCurrency,
    enabledTransferSystems: apiConfig.integration?.enabled_transfer_systems ?? DEFAULT_UI_CONFIG.enabledTransferSystems,
    sarieInstantTransfer: apiConfig.integration?.sarie_instant_transfer ?? DEFAULT_UI_CONFIG.sarieInstantTransfer,
    sadadBillPayment: apiConfig.integration?.sadad_bill_payment ?? DEFAULT_UI_CONFIG.sadadBillPayment,
    bankApiIntegration: apiConfig.integration?.bank_api_enabled ?? DEFAULT_UI_CONFIG.bankApiIntegration,
    statementImportFormat: apiConfig.integration?.statement_import_format ?? DEFAULT_UI_CONFIG.statementImportFormat,
    defaultBankCode: apiConfig.integration?.default_bank_code ?? DEFAULT_UI_CONFIG.defaultBankCode,
    dateFormat: apiConfig.display?.date_format ?? DEFAULT_UI_CONFIG.dateFormat,
  };
}

export function mapConfigToApi(flatConfig, previousApi = {}) {
  return {
    ...previousApi,
    auto_reconciliation: {
      ...(previousApi.auto_reconciliation || {}),
      enabled: flatConfig.autoReconciliation,
      match_tolerance: (flatConfig.reconciliationTolerance || 1000) / 1000,
      date_tolerance: previousApi.auto_reconciliation?.date_tolerance ?? 3,
    },
    notifications: {
      ...(previousApi.notifications || {}),
      low_balance_alert: previousApi.notifications?.low_balance_alert ?? true,
      low_balance_threshold: flatConfig.lowBalanceThreshold ?? previousApi.notifications?.low_balance_threshold ?? DEFAULT_UI_CONFIG.lowBalanceThreshold,
      high_transaction_alert: previousApi.notifications?.high_transaction_alert ?? true,
      high_transaction_threshold: flatConfig.highTransactionThreshold ?? previousApi.notifications?.high_transaction_threshold ?? DEFAULT_UI_CONFIG.highTransactionThreshold,
      unusual_activity_alert: previousApi.notifications?.unusual_activity_alert ?? true,
      unusual_activity_threshold: flatConfig.unusualActivityThreshold ?? previousApi.notifications?.unusual_activity_threshold ?? DEFAULT_UI_CONFIG.unusualActivityThreshold,
      pending_reconciliation_alert: flatConfig.alertOnMismatch,
      pending_reconciliation_days: previousApi.notifications?.pending_reconciliation_days ?? 7,
      old_check_alert: flatConfig.alertExpiringChecks,
      old_check_days: flatConfig.expiringChecksDays || flatConfig.checkClearanceDays,
    },
    fees: {
      ...(previousApi.fees || {}),
      default_transfer_fee: flatConfig.feeThreshold ?? previousApi.fees?.default_transfer_fee ?? 100,
      default_check_fee: previousApi.fees?.default_check_fee ?? 50,
      default_maintenance_fee: previousApi.fees?.default_maintenance_fee ?? 200,
    },
    reconciliation: {
      ...(previousApi.reconciliation || {}),
      auto_match_on_import: flatConfig.autoImportStatements,
      require_approval: previousApi.reconciliation?.require_approval ?? false,
      allow_manual_adjustment: previousApi.reconciliation?.allow_manual_adjustment ?? true,
    },
    display: {
      ...(previousApi.display || {}),
      default_currency: flatConfig.defaultCurrency || getBrandDefaultCurrency(),
      date_format: flatConfig.dateFormat,
      number_format: getBrandLocale(),
    },
    integration: {
      ...(previousApi.integration || {}),
      enabled_transfer_systems: flatConfig.enabledTransferSystems || DEFAULT_UI_CONFIG.enabledTransferSystems,
      sarie_instant_transfer: flatConfig.sarieInstantTransfer ?? DEFAULT_UI_CONFIG.sarieInstantTransfer,
      sadad_bill_payment: flatConfig.sadadBillPayment ?? DEFAULT_UI_CONFIG.sadadBillPayment,
      bank_api_enabled: flatConfig.bankApiIntegration ?? DEFAULT_UI_CONFIG.bankApiIntegration,
      statement_import_format: flatConfig.statementImportFormat || DEFAULT_UI_CONFIG.statementImportFormat,
      default_bank_code: flatConfig.defaultBankCode || DEFAULT_UI_CONFIG.defaultBankCode,
    },
    security: {
      ...(previousApi.security || {}),
      require_approval_for_large_transfers: true,
      large_transfer_threshold: flatConfig.requireApprovalAbove || flatConfig.maxTransferAmount || 100000,
      require_dual_authorization: flatConfig.requireDualApproval || flatConfig.requireTwoFactorAuth,
    },
    backup: previousApi.backup || {
      auto_backup_enabled: true,
      backup_frequency_days: 7,
      backup_retention_days: 30,
    },
    _ui: { ...flatConfig },
  };
}

export function accountsById(accounts = []) {
  return Object.fromEntries((accounts || []).map((a) => [a.id, a]));
}

export function mapBankFeeFromApi(row) {
  if (!row) return row;
  return {
    id: row.id,
    date: row.fee_date || '',
    banque: row.bank_name || row.account_name || '',
    type: row.fee_type || '',
    montant: Number(row.amount) || 0,
    reference: row.reference || '',
    description: row.description || '',
    account_id: row.account_id,
    account_name: row.account_name || '',
    account_number: row.account_number || '',
    bank_name: row.bank_name || '',
  };
}

export function mapBankFeeToApi(form, accounts = []) {
  const resolveAccountId = (explicitId, label) => {
    if (explicitId != null && explicitId !== '') return Number(explicitId);
    const normalized = String(label || '').trim();
    if (!normalized) return null;
    const match = accounts.find((a) => {
      const dashLabel = `${a.bank_name || ''} — ${a.account_name || a.account_number || ''}`.trim();
      const spaceLabel = `${a.bank_name || ''} ${a.account_name || ''}`.trim();
      return (
        a.bank_name === normalized ||
        a.account_name === normalized ||
        spaceLabel === normalized ||
        dashLabel === normalized ||
        String(a.id) === normalized
      );
    });
    return match ? match.id : null;
  };

  return {
    account_id: resolveAccountId(form.account_id, form.banque),
    fee_type: form.type,
    amount: parseFloat(form.montant) || 0,
    fee_date: form.date,
    description: form.description || null,
    reference: form.reference || null,
  };
}

const DISCREPANCY_TYPE_TO_UI = {
  missing_in_book: 'missing_accounting',
  missing_in_statement: 'missing_bank',
  amount_mismatch: 'different_amounts',
};

const DISCREPANCY_STATUS_TO_UI = {
  open: 'unresolved',
  pending: 'unresolved',
  in_progress: 'analysis',
  resolved: 'resolved',
  ignored: 'resolved',
};

export function mapDiscrepancyFromApi(row, accountsByIdMap = {}) {
  if (!row) return row;
  const account =
    accountsByIdMap[row.account_id] ||
    accountsByIdMap[row.reconciliation_account_id] ||
    {};
  const uiType = DISCREPANCY_TYPE_TO_UI[row.type] || row.type;
  const entryStub = {
    id: row.id,
    date: row.transaction_date || '',
    reference: row.reference || '',
    description: row.description || '',
    amount: Number(row.amount) || 0,
  };

  return {
    id: row.id,
    accountId: row.account_id || account.id,
    bank: account.bank_name || row.bank_name || '',
    accountNumber: account.account_number || row.account_number || '',
    date: row.transaction_date || '',
    amount: Number(row.amount) || 0,
    status: DISCREPANCY_STATUS_TO_UI[row.status] || row.status || 'unresolved',
    type: uiType,
    description: row.description || '',
    resolutionNotes: row.resolution_notes || '',
    bankEntry: uiType === 'missing_bank' || uiType === 'different_amounts' ? entryStub : null,
    accountingEntry: uiType === 'missing_accounting' || uiType === 'different_amounts' ? entryStub : null,
    apiStatus: row.status,
  };
}

export function mapBankStatementToReconciliationEntry(row) {
  if (!row) return row;
  const credit = Number(row.credit) || 0;
  const debit = Number(row.debit) || 0;
  return {
    id: row.id,
    date: row.transaction_date || row.statement_date || '',
    reference: row.reference || '',
    description: row.description || '',
    amount: credit > 0 ? credit : debit > 0 ? -debit : 0,
  };
}

export function mapAccountingToReconciliationEntry(row) {
  if (!row) return row;
  const credit = Number(row.credit) || 0;
  const debit = Number(row.debit) || 0;
  const amount = credit > 0 ? credit : debit > 0 ? -debit : Number(row.montant_ttc) || 0;
  return {
    id: row.id,
    date: row.date || '',
    reference: row.reference || '',
    description: row.description || '',
    amount,
    account: row.category || '',
    accountName: row.affectation || row.bank_name || row.account_name || '',
  };
}

const DOC_LINE_TO_API = {
  quote: 'devis_num',
  proforma: 'proforma_num',
  purchaseOrder: 'bon_commande_num',
  deliveryNote: 'bon_livraison_num',
};

/** Map banking_accounting_entries API row → AccountingPage UI fields. */
export function mapAccountingEntryFromApi(row) {
  if (!row) return row;
  const debit = Number(row.debit) || 0;
  const credit = Number(row.credit) || 0;
  let amount = Number(row.montant_ttc) || 0;
  if (credit > 0) amount = credit;
  else if (debit > 0) amount = -debit;

  return {
    id: row.id,
    date: row.date || '',
    dateCheq: row.date_cheque || '',
    dateRelv: row.date_releve || row.date || '',
    reference: row.reference || '',
    description: row.description || '',
    amount,
    affectation: row.affectation || '',
    orderRequestNum: row.demande_offre_num || '',
    proformaNum: row.proforma_num || '',
    quoteNum: row.devis_num || '',
    purchaseOrderNum: row.bon_commande_num || '',
    deliveryNoteNum: row.bon_livraison_num || '',
    attachmentNum: row.attachment_num || '',
    paie: row.paie || '',
    transferOrderNum: row.ordre_virement_num || '',
    chequeNum: row.cheque_num || '',
    supplierName: row.supplier_name || '',
    supplierAddress: row.supplier_address || '',
    taxId: row.tax_id || '',
    commercialRegister: row.commercial_register || '',
    category: row.category || '',
    reconciled: row.reconciled === 1 || row.reconciled === true,
    bankFees: Number(row.bank_fees ?? row.frais_bancaires) || 0,
    account_id: row.account_id,
  };
}

function normalizeAffectationToApi(val) {
  const v = String(val || '').toLowerCase();
  if (v === 'charges' || v === 'charge') return 'Charges';
  if (v === 'produits' || v === 'produit' || v === 'products') return 'Produits';
  return val || null;
}

/** Map AccountingPage form → banking_accounting_entries API payload. */
export function mapAccountingEntryToApi(form, docLinkLines = []) {
  const rawAmount = parseFloat(form.amount) || 0;
  const type = String(form.type || '').toLowerCase();
  const isDebit = type === 'debit' || (type !== 'credit' && rawAmount < 0);
  const absAmount = Math.abs(rawAmount);

  const docFields = {};
  (docLinkLines || []).forEach((line) => {
    const apiKey = DOC_LINE_TO_API[line.kind];
    if (apiKey && line.number) docFields[apiKey] = line.number;
  });

  return {
    date: form.date,
    date_cheque: form.dateCheq || null,
    date_releve: form.dateRelv || form.date,
    reference: form.reference,
    description: form.description || null,
    affectation: normalizeAffectationToApi(form.allocation),
    demande_offre_num: form.orderRequestNum || null,
    proforma_num: docFields.proforma_num || form.proformaNum || null,
    devis_num: docFields.devis_num || form.quoteNum || null,
    bon_commande_num: docFields.bon_commande_num || form.purchaseOrderNum || null,
    bon_livraison_num: docFields.bon_livraison_num || form.deliveryNoteNum || null,
    attachment_num: form.attachmentNum || null,
    paie: form.paie || null,
    ordre_virement_num: form.transferOrderNum || null,
    cheque_num: form.chequeNum || null,
    supplier_name: form.supplierName || null,
    supplier_address: form.supplierAddress || null,
    tax_id: form.taxId || null,
    commercial_register: form.commercialRegister || null,
    debit: isDebit ? absAmount : 0,
    credit: !isDebit ? absAmount : 0,
    montant_ttc: absAmount,
    category: form.category || null,
    reconciled: form.reconciled ? 1 : 0,
    account_id: form.account_id ? Number(form.account_id) : null,
    ...docFields,
  };
}

/** Populate AccountingPage form state from a UI entry row. */
export function mapAccountingEntryToForm(entry) {
  if (!entry) return null;
  const amount = Number(entry.amount) || 0;
  return {
    reference: entry.reference || '',
    description: entry.description || '',
    amount: String(Math.abs(amount) || ''),
    type: amount < 0 ? 'debit' : 'credit',
    allocation: String(entry.affectation || '').toLowerCase() === 'charges' ? 'charges' : 'produits',
    date: entry.date || '',
    dateCheq: entry.dateCheq || '',
    dateRelv: entry.dateRelv || entry.date || '',
    supplierName: entry.supplierName || '',
    supplierAddress: entry.supplierAddress || '',
    taxId: entry.taxId || '',
    commercialRegister: entry.commercialRegister || '',
    paie: entry.paie || '',
    chequeNum: entry.chequeNum || '',
    transferOrderNum: entry.transferOrderNum || '',
    orderRequestNum: entry.orderRequestNum || '',
    proformaNum: entry.proformaNum || '',
    quoteNum: entry.quoteNum || '',
    purchaseOrderNum: entry.purchaseOrderNum || '',
    deliveryNoteNum: entry.deliveryNoteNum || '',
    attachmentNum: entry.attachmentNum || '',
    reconciled: !!entry.reconciled,
    account_id: entry.account_id || '',
  };
}
